import { jwtDecode } from 'jwt-decode';

import { invalidateSession } from './invalidateSession';

/**
 * Subset of the feathers app surface this module relies on. Typed as a
 * structural minimum so `setupSessionLifecycle` can be called from
 * `getApp.ts` without creating an `import/no-cycle` violation between
 * `sessionLifecycle.ts` and `getApp.ts`.
 *
 * `'authenticated'` handlers receive the `authResult` payload that
 * `@feathersjs/authentication-client` emits on successful login. We
 * read `accessToken` from there directly (rather than from
 * `localStorage`) because the event fires *before* the client's
 * `setAccessToken` call writes the JWT to storage — see the comment on
 * `onAuthenticated` for the full reasoning.
 */
type AuthResult = { accessToken?: string };
type LifecycleApp = {
  on(event: 'authenticated', handler: (authResult: AuthResult) => void): void;
  on(event: 'logout', handler: () => void): void;
  logout(): Promise<unknown>;
};

/**
 * Captured at setup so the timer-driven `scheduleTimer` and the
 * `visibilitychange` listener can call `invalidateSession(boundApp)`
 * without re-resolving `getApp()`. Module-scoped (rather than passed
 * through `scheduleTimer`) because `setTimeout(scheduleTimer, …)`
 * cannot easily forward an argument.
 */
let boundApp: LifecycleApp | null = null;

/**
 * How far before the JWT's `exp` to proactively kick the operator to the
 * PIN screen. 30 s is comfortably larger than any conceivable clock skew
 * between the iPad and feathers-bot, while still being small enough that
 * an operator who logs in right before TTL gets the bulk of their session.
 */
const JWT_EXPIRY_BUFFER_MS = 30_000;

/**
 * Cap on a single `setTimeout` interval. Browsers — Safari WebKit in
 * particular, on iPad — may throttle or coalesce long-duration timers, so
 * we re-arm every 5 minutes for a long session rather than relying on a
 * single 24-hour `setTimeout` firing accurately.
 */
const SESSION_TIMER_REARM_MS = 5 * 60_000;

let currentTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

function clearTimer(): void {
  if (currentTimer !== null) {
    clearTimeout(currentTimer);
    currentTimer = null;
  }
}

function invalidateSessionBound(): void {
  if (boundApp === null) {
    // Should be unreachable: `boundApp` is set by `setupSessionLifecycle`
    // before any of the lifecycle handlers can fire. Throwing here makes
    // the bug loud if a future refactor ever reaches this branch.
    throw new Error(
      'invalidateSessionBound called before setupSessionLifecycle',
    );
  }

  invalidateSession(boundApp);
}

function scheduleTimer(tokenOverride?: string): void {
  clearTimer();

  // Prefer the token the caller already has in hand — the `'authenticated'`
  // event handler passes the JWT straight from `authResult` because
  // `localStorage` is not yet populated at the moment that event fires
  // (feathers-client 4.5 emits the event *before* `setAccessToken`).
  const token = tokenOverride ?? localStorage.getItem('feathers-jwt');

  if (!token) {
    return;
  }

  let exp: number | undefined;

  try {
    ({ exp } = jwtDecode<{ exp?: number }>(token));
  } catch {
    // Malformed token → kick immediately. Should be vanishingly rare in
    // practice (feathers-bot only ever issues well-formed JWTs), but we
    // fail closed if e.g. someone hand-edits localStorage.
    invalidateSessionBound();

    return;
  }

  if (!exp) {
    // No `exp` claim → treat as invalid and kick. Same reasoning as the
    // malformed-token branch.
    invalidateSessionBound();

    return;
  }

  const msUntilKick = exp * 1000 - Date.now() - JWT_EXPIRY_BUFFER_MS;

  if (msUntilKick <= 0) {
    // Already inside the buffer window (or past expiry entirely) — kick now.
    invalidateSessionBound();

    return;
  }

  // Cap each setTimeout at SESSION_TIMER_REARM_MS so long sessions
  // re-evaluate periodically rather than depending on a single multi-hour
  // timer firing accurately.
  const wait = Math.min(msUntilKick, SESSION_TIMER_REARM_MS);

  currentTimer = setTimeout(scheduleTimer, wait);
}

function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible') {
    return;
  }

  // Delegate to `scheduleTimer`. It re-evaluates the JWT against
  // `JWT_EXPIRY_BUFFER_MS` and either:
  //   - kicks immediately if we are already inside the buffer window
  //     (`exp − BUFFER ≤ now`), which is exactly the case a previous
  //     visibility-check using only `isFeathersJwtValid()` would have
  //     *missed* — JWT still decodes as valid for another ~30 s but the
  //     primary timer should have fired,
  //   - or re-arms a fresh `setTimeout` against the remaining lifetime,
  //     self-healing a missed timer slot (e.g. backgrounded tab
  //     throttling).
  // No token in localStorage is also handled there (early return).
  scheduleTimer();
}

/**
 * `'authenticated'` listener for `@feathersjs/authentication-client`.
 *
 * Why we use the `authResult.accessToken` instead of reading
 * `localStorage` from inside `scheduleTimer()`:
 *
 * `@feathersjs/authentication-client@4.5.11` emits `'authenticated'`
 * synchronously inside the `.then()` of `service.create(...)`, on the
 * line immediately *before* the `setAccessToken(accessToken)` call —
 *
 *     this.app.emit('login', authResult);
 *     this.app.emit('authenticated', authResult);   // ← our listener
 *     return this.setAccessToken(accessToken)...    // ← writes localStorage
 *
 * If we read `localStorage.getItem('feathers-jwt')` from the listener,
 * we observe either `null` (fresh first login) or the *previous*
 * session's stale JWT. `scheduleTimer` would then return early and the
 * timer would never arm — exactly the SUP-3915 failure mode this
 * mechanism is supposed to prevent. Reading the token straight from the
 * event payload sidesteps the race entirely.
 */
function onAuthenticated(authResult: AuthResult): void {
  if (typeof authResult?.accessToken !== 'string') {
    return;
  }

  scheduleTimer(authResult.accessToken);
}

/**
 * Install the session-expiry kick. Idempotent: subsequent calls are a
 * no-op so a duplicate boot path cannot accumulate listeners on the
 * feathers app.
 *
 * Takes the feathers app as a parameter (rather than importing
 * `getApp()` directly) to keep this module a leaf in the import graph:
 * `getApp.ts` calls `setupSessionLifecycle(app)`, which avoids an
 * `import/no-cycle` violation between the two files.
 *
 * Registers:
 * - A self-re-arming `setTimeout` that fires near `exp` and calls
 *   `invalidateSession()`.
 * - A `visibilitychange` listener that catches up if the tab was
 *   suspended past the scheduled fire time.
 * - `app.on('authenticated', scheduleTimer)` so re-login restarts the
 *   countdown against the fresh JWT.
 * - `app.on('logout', clearTimer)` so cleanup happens via the auth
 *   event the feathers-client already fires.
 *
 * No teardown is exported: the feathers-client is a singleton that
 * lives for the lifetime of the page, and `initialized` makes
 * re-registration safe across hot-reload or multiple-import scenarios
 * in tests.
 */
export function setupSessionLifecycle(app: LifecycleApp): void {
  if (initialized) {
    return;
  }

  initialized = true;
  boundApp = app;

  scheduleTimer();

  document.addEventListener('visibilitychange', onVisibilityChange);

  // Note: invalidateSession() calls app.logout(), which fires the
  // 'logout' event below — so the timer is normally cleared *via* the
  // event, not directly. If logout() rejects (e.g. socket already gone),
  // the event does not fire, but invalidateSession() has already cleared
  // the JWT and PrivatePage will navigate away regardless. Any dangling
  // timer is benign: on its next fire scheduleTimer sees an absent token
  // and returns without doing anything.
  app.on('authenticated', onAuthenticated);
  app.on('logout', clearTimer);
}
